import HttpService from './http-service';
import EdudoResponse from '../models/edudo-response';

export interface UploadResult {
  successful: UploadFilesResponse[];
  failed: { file: File; error: any }[];
}

export default class GeneralService {
  // Core upload logic shared by both methods
  private async uploadFilesInBatches(
    files: FileList,
    onProgress?: (uploaded: number, total: number) => void
  ): Promise<UploadResult> {
    const filesArray = Array.from(files);
    const concurrencyLimit = 3;
    const successfulUploads: UploadFilesResponse[] = [];
    const failedFiles: { file: File; error: any }[] = [];
    let uploadedCount = 0;

    // Helper function to upload a single file
    const uploadSingleFile = async (file: File): Promise<UploadFilesResponse | null> => {
      try {
        const formData = new FormData();
        formData.append('files', file, file.name);

        const response = await HttpService.uploadFiles<EdudoResponse<UploadFilesResponse[]>>
          ('/v1/filemanagement/upload', formData);

        uploadedCount++;
        if (onProgress) {
          onProgress(uploadedCount, filesArray.length);
        }

        // Backend returns array with single file response
        return response.data[0];
      } catch (error) {
        uploadedCount++;
        if (onProgress) {
          onProgress(uploadedCount, filesArray.length);
        }
        failedFiles.push({ file, error });
        return null;
      }
    };

    // Process files in parallel batches with concurrency limit
    for (let i = 0; i < filesArray.length; i += concurrencyLimit) {
      const batch = filesArray.slice(i, i + concurrencyLimit);
      const results = await Promise.all(batch.map(uploadSingleFile));

      // Collect successful uploads
      results.forEach(result => {
        if (result) {
          successfulUploads.push(result);
        }
      });
    }

    return {
      successful: successfulUploads,
      failed: failedFiles
    };
  }

  // Original method with concurrent uploads (all-or-nothing behavior)
  async uploadFile(
    files: FileList
  ): Promise<UploadFilesResponse[]> {
    const result = await this.uploadFilesInBatches(files);

    // If any files failed, throw error (all-or-nothing behavior)
    if (result.failed.length > 0) {
      const failedNames = result.failed.map(f => f.file.name).join(', ');
      throw new Error(
        `Failed to upload ${result.failed.length} file(s): ${failedNames}`
      );
    }

    return result.successful;
  }

  async updateProfileItem(type: 'name' | 'email' | 'password', value: string) {
    const newData = {};
    newData[type] = value;

    const response = await HttpService.patchJson<EdudoResponse<Employee>>('/v1/Employees/Profile', newData);

    return response.data;
  }
}